const POSTHOG_SCRIPT_ID = 'posthog-js'
const CONSENT_COOKIE = 'gms_analytics_consent'
const CONSENT_EVENT = 'gms:analytics-consent-change'
const OPEN_PREFERENCES_EVENT = 'gms:analytics-consent-open'
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

let initialized = false
let scriptLoadingPromise = null

function getConfig() {
  return {
    key: import.meta.env.VITE_POSTHOG_KEY || '',
  }
}

function getPosthog() {
  return window.posthog || null
}

export function isAnalyticsConfigured() {
  return Boolean(getConfig().key)
}

function cookieDomain(hostname = window.location.hostname) {
  if (hostname === 'gmsomeday.com' || hostname.endsWith('.gmsomeday.com')) {
    return '.gmsomeday.com'
  }
  return ''
}

export function getAnalyticsConsent() {
  if (typeof document === 'undefined') return null
  const raw = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${CONSENT_COOKIE}=`))
    ?.split('=')
    .slice(1)
    .join('=')
  const value = raw ? decodeURIComponent(raw) : ''
  if (value === 'granted' || value === 'denied') return value
  return null
}

function writeAnalyticsConsent(consent) {
  const domain = cookieDomain()
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = [
    `${CONSENT_COOKIE}=${encodeURIComponent(consent)}`,
    `Max-Age=${CONSENT_MAX_AGE_SECONDS}`,
    'Path=/',
    'SameSite=Lax',
    domain ? `Domain=${domain}` : '',
    secure,
  ].filter(Boolean).join('; ')
}

function dispatchConsentChange(consent) {
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { consent } }))
}

export function onAnalyticsConsentChange(handler) {
  window.addEventListener(CONSENT_EVENT, handler)
  return () => window.removeEventListener(CONSENT_EVENT, handler)
}

export function openAnalyticsConsentPreferences() {
  window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES_EVENT))
}

export function onOpenAnalyticsConsentPreferences(handler) {
  window.addEventListener(OPEN_PREFERENCES_EVENT, handler)
  return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, handler)
}

function loadScript() {
  if (scriptLoadingPromise) return scriptLoadingPromise

  scriptLoadingPromise = new Promise((resolve, reject) => {
    if (getPosthog() || document.getElementById(POSTHOG_SCRIPT_ID)) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.id = POSTHOG_SCRIPT_ID
    script.async = true
    script.crossOrigin = 'anonymous'
    script.src = `${window.location.origin}/rly/static/array.js`
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load PostHog script'))
    document.head.appendChild(script)
  })

  return scriptLoadingPromise
}

function applyConsentToPosthog(consent) {
  const posthog = getPosthog()
  if (!posthog) return
  if (consent === 'granted') posthog.opt_in_capturing?.()
  if (consent === 'denied') posthog.opt_out_capturing?.()
}

function attributionProps(path = window.location.pathname) {
  const params = new URLSearchParams(window.location.search)
  return {
    current_path: path,
    app_host: window.location.host,
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_term: params.get('utm_term') || undefined,
    utm_content: params.get('utm_content') || undefined,
    referrer: document.referrer || undefined,
  }
}

export async function initAnalytics() {
  const { key } = getConfig()
  if (!key || typeof window === 'undefined') return

  await loadScript()
  const posthog = getPosthog()
  if (!posthog || initialized) return

  posthog.init(key, {
    api_host: `${window.location.origin}/rly`,
    ui_host: 'https://us.posthog.com',
    defaults: '2026-05-30',
    cookieless_mode: 'on_reject',
    capture_pageview: false,
    persistence: 'localStorage+cookie',
    person_profiles: 'identified_only',
    session_recording: { maskAllInputs: true },
  })
  posthog.register({ product: 'gmsomeday', surface: 'app' })
  applyConsentToPosthog(getAnalyticsConsent())
  initialized = true
}

export async function setAnalyticsConsent(consent) {
  if (consent !== 'granted' && consent !== 'denied') {
    throw new Error(`Unsupported analytics consent: ${consent}`)
  }
  writeAnalyticsConsent(consent)
  try {
    await initAnalytics()
    applyConsentToPosthog(consent)
  } catch {
    // Consent is a user preference first; script blockers or a missing local
    // /rly proxy must not prevent saving it.
  }
  dispatchConsentChange(consent)
}

export function trackPageview(path) {
  const posthog = getPosthog()
  if (!posthog) return
  posthog.capture('$pageview', {
    ...attributionProps(path),
    $current_url: window.location.origin + path,
  })
}

export function trackEvent(event, props = {}) {
  const posthog = getPosthog()
  if (!posthog) return
  posthog.capture(event, {
    ...attributionProps(),
    ...props,
  })
}
